import React, { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { useAppDispatch } from '@/store/hooks'
import { loginUser } from '@/store/thunks/authThunks'
import { setEntraAuthSuccess } from '@/store/slices/authSlice'
import entraAuthService from '@/services/entraAuthService'
import { transformUser } from '@/utils/userTransform'

const EntraLoginButton: React.FC = () => {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const lastClickTime = useRef<number>(0)

  const handleLogin = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Debounce rapid clicks (prevent multiple clicks within 2 seconds)
    const now = Date.now()
    if (now - lastClickTime.current < 2000) {
      console.log('⚠️ Click too recent, ignoring')
      return
    }
    lastClickTime.current = now
    
    if (loading) {
      console.log('⚠️ Login already in progress, ignoring click')
      return
    }
    
    setLoading(true)
    try {
      console.log('🔄 Starting Entra ID login...')
      
      // Check if already signed in with valid tokens
      const accounts = entraAuthService.getAllAccounts()
      console.log('📋 Current accounts:', accounts.length)
      
      if (accounts.length > 0) {
        console.log('👤 Found existing account, attempting silent authentication...')
        
        try {
          const account = accounts[0]
          entraAuthService.setActiveAccount(account)
          const token = await entraAuthService.getTokenSilently(account)
          const response = await entraAuthService.verifyTokenWithBackend(token)
          
          // Update Redux state
          const transformedUser = transformUser(response.user)
          dispatch(setEntraAuthSuccess({
            user: transformedUser,
            token: response.token
          }))
          
          console.log('✅ Silent authentication successful, redirecting...')
          window.location.href = '/'
          return
        } catch (silentError) {
          console.error('❌ Silent authentication failed:', silentError)
          console.log('⚠️ Proceeding with interactive login...')
          // Clear the failed account without triggering logout redirect
          entraAuthService.clearInteractionInProgress()
          entraAuthService.setActiveAccount(null)
        }
      }
      
      // Start interactive login with redirect
      console.log('🔄 Starting interactive login...')
      await entraAuthService.loginRedirect()
      // Note: loginRedirect will cause a page redirect, so code after this won't execute
      
    } catch (error) {
      console.error('❌ Login failed:', error)
      console.error('❌ Error details:', {
        name: (error as Error)?.name,
        message: (error as Error)?.message,
        errorCode: (error as any)?.errorCode,
        errorMessage: (error as any)?.errorMessage,
        subError: (error as any)?.subError,
        stack: (error as Error)?.stack
      })
      
      // Handle specific MSAL errors
      if ((error as Error)?.message?.includes('interaction_in_progress')) {
        console.log('⚠️ Interaction already in progress, clearing and retrying...')
        entraAuthService.clearInteractionInProgress()
        // Don't show alert, just reset loading state
      } else if ((error as Error)?.message) {
        alert(`Login failed: ${(error as Error).message}`)
      }
      
      setLoading(false)
    }
  }

  return (
    <Button 
      onClick={handleLogin}
      disabled={loading}
      className="w-full"
      size="lg"
    >
      {loading ? 'Signing in...' : 'Sign in with Microsoft'}
    </Button>
  )
}

export default EntraLoginButton